import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { ApiError } from "../api/client";
import { mfaClient, setMfaClientSession } from "../api/mfaAuthClient";
import { revokeMfaFactor } from "../api/mfaFactorRevocation";
import { Button } from "../components/Button";
import { StatusBanner } from "../components/StatusBanner";

type ViewState = "loading" | "ready" | "revoking" | "success" | "error";

function messageForError(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message;
  return "Não foi possível revogar o autenticador agora. Tente novamente.";
}

async function findVerifiedFactor(accessToken: string, refreshToken: string): Promise<string> {
  await setMfaClientSession(accessToken, refreshToken);
  const { data, error } = await mfaClient.auth.mfa.listFactors();
  if (error) throw error;
  const factor = (data?.totp ?? []).find((candidate) => candidate.factor_type === "totp" && candidate.status === "verified");
  if (!factor) throw new Error("Não há um autenticador TOTP verificado disponível para revogar.");
  return factor.id;
}

export function MfaRevocationPage() {
  const { state, expireSession } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<ViewState>("loading");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (state.status !== "authenticated") return;
    let cancelled = false;
    void findVerifiedFactor(state.accessToken, state.refreshToken)
      .then((currentFactorId) => {
        if (cancelled) return;
        setFactorId(currentFactorId);
        setView("ready");
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        if ((cause as { status?: number }).status === 401) {
          expireSession();
          return;
        }
        setError(messageForError(cause));
        setView("error");
      });
    return () => { cancelled = true; };
  }, [expireSession, state.status === "authenticated" ? state.accessToken : null]);

  if (state.status !== "authenticated") return null;
  const authenticatedState = state;

  async function revoke() {
    if (!factorId || view === "revoking") return;
    if (authenticatedState.aal.currentLevel !== "aal2") {
      navigate(`/mfa/desafio?retorno=${encodeURIComponent("/mfa/revogar")}`);
      return;
    }
    if (!window.confirm("Revogar o autenticador atual? Você precisará entrar novamente.")) return;
    setError(null);
    setView("revoking");
    try {
      await revokeMfaFactor(authenticatedState.accessToken, factorId);
      setView("success");
      expireSession();
      navigate("/login", { replace: true, state: { reason: "mfa-revoked" } });
    } catch (cause: unknown) {
      if ((cause as { status?: number }).status === 401) {
        expireSession();
        navigate("/login", { replace: true });
        return;
      }
      setError(messageForError(cause));
      setView("error");
    }
  }

  return (
    <main className="centered-page mfa-challenge-page">
      <section className="card mfa-challenge-card" aria-labelledby="mfa-revocation-title">
        <p className="mfa-challenge__eyebrow">Segurança da conta</p>
        <h1 id="mfa-revocation-title">Revogar autenticador</h1>
        <p className="card__subtitle">Esta ação remove o autenticador atual e encerra suas sessões ativas. Será necessário cadastrar outro autenticador no próximo acesso.</p>
        {error && <StatusBanner tone="error">{error}</StatusBanner>}
        {view === "loading" && <p role="status">Verificando seu autenticador…</p>}
        {view === "ready" && <Button type="button" onClick={() => void revoke()}>Revogar autenticador</Button>}
        {view === "revoking" && <p role="status">Revogando autenticador e encerrando sessões…</p>}
        {view === "success" && <p role="status">Autenticador revogado. Redirecionando para o login…</p>}
        {view === "error" && <Button type="button" onClick={() => void revoke()}>Tentar novamente</Button>}
      </section>
    </main>
  );
}
